/**
 * Procedural storm audio layers.
 *
 * StormAudio sits on top of the shared `AudioSystem` and adds four layers that
 * rise and fall with a 0..1 storm intensity:
 *
 *   1. Wind        — looping bandpassed noise with a slow LFO "gust", brighter
 *                    and louder as the storm builds.
 *   2. Rain        — looping bandpassed hiss whose brightness tracks intensity.
 *   3. Thunder     — occasional one-shot lowpass rumbles from a small voice
 *                    pool; intervals shorten as the storm grows.
 *   4. Timber      — occasional bandpassed groans (wood under stress), also
 *                    pooled and scheduled non-repetitively.
 *
 * Every layer connects to `AudioSystem`'s pre-fade master gain, so it inherits
 * the master volume and the first-gesture fade-in. All randomness flows through
 * `createSeededRandom`, so a given intensity sequence is reproducible.
 */

import { AudioSystem } from './AudioSystem';
import { createSeededRandom } from '../utils/random';

const STORM_AUDIO_SEED = 0x5a17;
const WIND_BUFFER_SECONDS = 5;
const RAIN_BUFFER_SECONDS = 4;
const THUNDER_BUFFER_SECONDS = 8;
const TIMBER_BUFFER_SECONDS = 3;
const VOICE_COUNT = 3;

/** Persistent filter + gain for one pooled one-shot voice. */
interface OneShotVoice {
  filter: BiquadFilterNode;
  gain: GainNode;
  source: AudioBufferSourceNode | null;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export class StormAudio {
  private readonly base: AudioSystem;
  private readonly ownsBase: boolean;
  private readonly reducedMotion: boolean;
  private readonly rng = createSeededRandom(STORM_AUDIO_SEED);

  private storm = 0;
  private built = false;

  private windBuffer: AudioBuffer | null = null;
  private rainBuffer: AudioBuffer | null = null;
  private thunderBuffer: AudioBuffer | null = null;
  private timberBuffer: AudioBuffer | null = null;

  private windSource: AudioBufferSourceNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private windGain: GainNode | null = null;
  private windLfo: OscillatorNode | null = null;
  private windLfoGain: GainNode | null = null;

  private rainSource: AudioBufferSourceNode | null = null;
  private rainFilter: BiquadFilterNode | null = null;
  private rainGain: GainNode | null = null;

  private readonly thunderVoices: OneShotVoice[] = [];
  private readonly timberVoices: OneShotVoice[] = [];
  private thunderVoiceIndex = 0;
  private timberVoiceIndex = 0;

  private thunderTimer = 2;
  private timberTimer = 1;

  constructor(base?: AudioSystem) {
    this.base = base ?? new AudioSystem();
    this.ownsBase = base === undefined;
    this.reducedMotion =
      typeof window !== 'undefined' && 'matchMedia' in window
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
  }

  /** Drive every layer from a 0..1 storm intensity; 0 is fully silent. */
  setStorm(storm: number): void {
    this.storm = clamp01(storm);
    if (!this.built) return;
    const context = this.base.getContext();
    if (context) this.applyContinuous(context.currentTime);
  }

  /** Per-frame update; safe to call before the shared context exists. */
  update(delta: number): void {
    if (!this.ensureBuilt()) return;
    const context = this.base.getContext();
    if (!context) return;
    const now = context.currentTime;
    this.applyContinuous(now);
    if (!this.reducedMotion) {
      this.updateThunder(delta, this.storm, now);
      this.updateTimber(delta, this.storm, now);
    }
  }

  dispose(): void {
    const stop = (node: AudioScheduledSourceNode | null) => {
      if (!node) return;
      try {
        node.stop();
      } catch {
        // Already stopped or never started.
      }
      node.disconnect();
    };

    stop(this.windSource);
    stop(this.rainSource);
    stop(this.windLfo);

    this.windSource = null;
    this.rainSource = null;
    this.windLfo = null;
    this.windFilter?.disconnect();
    this.windGain?.disconnect();
    this.windLfoGain?.disconnect();
    this.rainFilter?.disconnect();
    this.rainGain?.disconnect();
    this.windFilter = null;
    this.windGain = null;
    this.windLfoGain = null;
    this.rainFilter = null;
    this.rainGain = null;

    for (const voice of [...this.thunderVoices, ...this.timberVoices]) {
      if (voice.source) {
        try {
          voice.source.stop();
        } catch {
          // Already stopped.
        }
        voice.source.disconnect();
      }
      voice.filter.disconnect();
      voice.gain.disconnect();
    }
    this.thunderVoices.length = 0;
    this.timberVoices.length = 0;

    this.windBuffer = null;
    this.rainBuffer = null;
    this.thunderBuffer = null;
    this.timberBuffer = null;
    this.built = false;

    if (this.ownsBase) this.base.dispose();
  }

  /** Build the graph once the shared context and master gain are available. */
  private ensureBuilt(): boolean {
    if (this.built) return true;
    const context = this.base.getContext();
    const master = this.base.getMasterGain();
    if (!context || !master) return false;
    this.buildGraph(context, master);
    this.built = true;
    return true;
  }

  private buildGraph(context: AudioContext, master: GainNode): void {
    this.windBuffer = this.createNoise(context, WIND_BUFFER_SECONDS);
    this.rainBuffer = this.createNoise(context, RAIN_BUFFER_SECONDS);
    this.thunderBuffer = this.createNoise(context, THUNDER_BUFFER_SECONDS);
    this.timberBuffer = this.createNoise(context, TIMBER_BUFFER_SECONDS);

    // --- Wind: looping bandpassed noise with a slow gust LFO ---------------
    this.windFilter = context.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 300;
    this.windFilter.Q.value = 0.5;
    this.windGain = context.createGain();
    this.windGain.gain.value = 0;
    this.windSource = context.createBufferSource();
    this.windSource.buffer = this.windBuffer;
    this.windSource.loop = true;
    this.windSource.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(master);
    this.windSource.start();

    this.windLfo = context.createOscillator();
    this.windLfo.type = 'sine';
    this.windLfo.frequency.value = 0.1;
    this.windLfoGain = context.createGain();
    this.windLfoGain.gain.value = 0;
    this.windLfo.connect(this.windLfoGain);
    this.windLfoGain.connect(this.windGain.gain);
    this.windLfo.start();

    // --- Rain: looping bandpassed hiss ------------------------------------
    this.rainFilter = context.createBiquadFilter();
    this.rainFilter.type = 'bandpass';
    this.rainFilter.frequency.value = 1800;
    this.rainFilter.Q.value = 0.35;
    this.rainGain = context.createGain();
    this.rainGain.gain.value = 0;
    this.rainSource = context.createBufferSource();
    this.rainSource.buffer = this.rainBuffer;
    this.rainSource.loop = true;
    this.rainSource.connect(this.rainFilter);
    this.rainFilter.connect(this.rainGain);
    this.rainGain.connect(master);
    this.rainSource.start();

    // --- Pooled one-shot voices -------------------------------------------
    for (let i = 0; i < VOICE_COUNT; i += 1) {
      this.thunderVoices.push(this.createVoice(context, master, 'lowpass', 160, 0.6));
      this.timberVoices.push(this.createVoice(context, master, 'bandpass', 220, 3));
    }
  }

  private createVoice(
    context: AudioContext,
    master: GainNode,
    type: BiquadFilterType,
    frequency: number,
    q: number,
  ): OneShotVoice {
    const filter = context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    const gain = context.createGain();
    gain.gain.value = 0;
    filter.connect(gain);
    gain.connect(master);
    return { filter, gain, source: null };
  }

  private applyContinuous(now: number): void {
    const storm = this.storm;
    // Prefer-reduced-motion still keeps the ambience, but softer and without
    // sudden one-shot events (those are skipped in update()).
    const scale = this.reducedMotion ? 0.55 : 1;
    const windLevel = storm * storm * 0.13 * scale;
    const rainLevel = storm * storm * 0.09 * scale;

    if (this.windGain) this.windGain.gain.setTargetAtTime(windLevel, now, 0.4);
    if (this.windFilter) {
      this.windFilter.frequency.setTargetAtTime(240 + storm * 640, now, 0.5);
    }
    if (this.windLfo) this.windLfo.frequency.setTargetAtTime(0.07 + storm * 0.13, now, 0.8);
    if (this.windLfoGain) {
      this.windLfoGain.gain.setTargetAtTime(windLevel * 0.7, now, 0.5);
    }
    if (this.rainGain) this.rainGain.gain.setTargetAtTime(rainLevel, now, 0.4);
    if (this.rainFilter) {
      this.rainFilter.frequency.setTargetAtTime(1400 + storm * 1600, now, 0.5);
    }
  }

  private updateThunder(delta: number, storm: number, now: number): void {
    if (storm <= 0.08) {
      this.thunderTimer = Math.max(this.thunderTimer, 1);
      return;
    }
    this.thunderTimer -= delta;
    if (this.thunderTimer > 0) return;

    const meanInterval = 14 - storm * 8; // ~14s light storm → ~6s full storm
    this.thunderTimer = meanInterval * (0.6 + this.rng() * 0.9);
    this.triggerThunder(now, storm);
  }

  private triggerThunder(now: number, storm: number): void {
    const context = this.base.getContext();
    const buffer = this.thunderBuffer;
    if (!context || !buffer) return;

    const voice = this.thunderVoices[this.thunderVoiceIndex];
    this.thunderVoiceIndex = (this.thunderVoiceIndex + 1) % this.thunderVoices.length;
    if (!voice) return;

    const source = context.createBufferSource();
    source.buffer = buffer;
    const offset = this.rng() * Math.max(0.1, buffer.duration - 3.1);
    const peak = (0.18 + this.rng() * 0.22) * storm;
    const duration = 1.4 + this.rng() * 1.6;

    voice.filter.frequency.setValueAtTime(130 + this.rng() * 140, now);
    const gain = voice.gain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(0.0001, now);
    gain.linearRampToValueAtTime(peak, now + 0.04);
    gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(voice.filter);
    source.start(now, offset);
    source.stop(now + duration + 0.05);
    voice.source = source;
    source.onended = () => {
      source.disconnect();
      if (voice.source === source) voice.source = null;
    };
  }

  private updateTimber(delta: number, storm: number, now: number): void {
    if (storm <= 0.25) {
      this.timberTimer = Math.max(this.timberTimer, 0.5);
      return;
    }
    this.timberTimer -= delta;
    if (this.timberTimer > 0) return;

    const meanInterval = 9 - storm * 6; // ~7s moderate → ~3s full storm
    this.timberTimer = meanInterval * (0.6 + this.rng() * 0.9);
    if (this.rng() < 0.25 + storm * 0.6) this.triggerTimber(now, storm);
  }

  private triggerTimber(now: number, storm: number): void {
    const context = this.base.getContext();
    const buffer = this.timberBuffer;
    if (!context || !buffer) return;

    const voice = this.timberVoices[this.timberVoiceIndex];
    this.timberVoiceIndex = (this.timberVoiceIndex + 1) % this.timberVoices.length;
    if (!voice) return;

    const source = context.createBufferSource();
    source.buffer = buffer;
    const offset = this.rng() * Math.max(0.1, buffer.duration - 1.4);
    const peak = (0.03 + this.rng() * 0.06) * storm;
    const duration = 0.5 + this.rng() * 0.8;

    voice.filter.frequency.setValueAtTime(140 + this.rng() * 260, now);
    voice.filter.Q.setValueAtTime(2 + this.rng() * 4, now);
    const gain = voice.gain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(0.0001, now);
    gain.linearRampToValueAtTime(peak, now + 0.03);
    gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(voice.filter);
    source.start(now, offset);
    source.stop(now + duration + 0.05);
    voice.source = source;
    source.onended = () => {
      source.disconnect();
      if (voice.source === source) voice.source = null;
    };
  }

  /** Mono seeded white-noise buffer for a continuous or one-shot layer. */
  private createNoise(context: AudioContext, seconds: number): AudioBuffer {
    const length = Math.max(1, Math.floor(context.sampleRate * seconds));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = this.rng() * 2 - 1;
    return buffer;
  }
}
